import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, Shield, UserCircle } from 'lucide-react';
import { useState } from 'react';

const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);  // Dropdown state

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
      setUserDropdownOpen(false);
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://i0.wp.com/periwinkle-labs.com/wp-content/uploads/2025/10/yellow-minimalist-world-smile-day-instagram-post-1-e1766753158415.png" 
              alt="Iniraa Logo" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/myiniraa"
              className="text-gray-700 hover:text-[#FF8C00] font-medium transition"
              data-testid="nav-community-link"
            >
              Community
            </Link>
            
            {isAdmin && (
              <Link
                to="/myiniraa/admin"
                className="text-gray-700 hover:text-[#FF8C00] font-medium transition flex items-center space-x-1"
                data-testid="nav-admin-link"
              >
                <Shield className="w-4 h-4" />
                <span>Admin</span>
              </Link>
            )}

            {isAuthenticated ? (
              <div className="flex items-center space-x-4 relative">
                {/* User Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-[#FF8C00] transition"
                    data-testid="user-dropdown-button"
                  >
                    <UserCircle className="w-5 h-5" />
                    <span className="text-sm font-medium" data-testid="nav-user-screenname">
                      {user?.screenname}
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <Link
                        to="/profile"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-[#FFF4E6] hover:text-[#FF8C00] transition"
                        data-testid="dropdown-profile-link"
                      >
                        <UserCircle className="w-4 h-4" />
                        <span>My Profile</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 w-full px-4 py-2 text-left text-gray-700 hover:bg-[#FFF4E6] hover:text-[#FF8C00] transition"
                        data-testid="dropdown-logout-button"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-[#FF8C00] font-medium transition"
                  data-testid="nav-login-link"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-[#FF8C00] text-white px-6 py-2 rounded-lg hover:bg-[#E67E00] transition font-medium"
                  data-testid="nav-register-link"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-700"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/myiniraa"
              className="block text-gray-700 hover:text-[#FF8C00] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Community
            </Link>
            
            {isAdmin && (
              <Link
                to="/myiniraa/admin"
                className="block text-gray-700 hover:text-[#FF8C00] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin Panel
              </Link>
            )}

            {isAuthenticated ? (
              <>
                <div className="text-sm text-gray-600 py-2">
                  Logged in as: {user?.screenname}
                </div>
                <Link
                  to="/profile"
                  className="block text-gray-700 hover:text-[#FF8C00] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left text-gray-700 hover:text-[#FF8C00] font-medium"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-gray-700 hover:text-[#FF8C00] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block bg-[#FF8C00] text-white px-4 py-2 rounded-lg hover:bg-[#E67E00] text-center font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
